// #1470: on a tablet, focusing an input in a modal makes iOS pan the visual
// viewport to keep the input above the on-screen keyboard, and the dialog —
// positioned against the layout viewport — slides off the top of the window.
// While any uib modal is open on a tablet, this holds every .modal-dialog
// inside the band the keyboard leaves visible, translateY ONLY: a dialog
// taller than the band keeps its natural height with the focused field slid
// into view (never capped or internally scrolled - Crane, 23 Sep, on device).
// ModalService (bootstrap-ui-modal.ts) drives modalOpened/modalClosed; on
// desktop isSystemTablet() is false and nothing here runs.

import { BrowserFeatureDetector } from '#gp/app-common/util/browser-feature-detector';
import { fitDialogToViewportBand, DialogFit, DialogNaturalMetrics, BAND_MARGIN_PX } from '#gp/app-common/util/modal-keyboard-fit';

/** What the manager needs from window.visualViewport — injectable so tests can
 * drive keyboard geometry WebKit cannot produce. */
export interface IViewportSource {
    readonly offsetTop: number;
    readonly offsetLeft: number;
    readonly height: number;
    readonly width: number;
    addEventListener(type: 'resize' | 'scroll', listener: () => void): void;
    removeEventListener(type: 'resize' | 'scroll', listener: () => void): void;
}

export interface IModalKeyboardAvoidanceDeps {
    getViewport(): IViewportSource | null | undefined;
    isTablet(): boolean;
    getLayoutViewportHeight(): number;
    getDialogElements(): HTMLElement[];
    addWindowResizeListener(listener: () => void): void;
    removeWindowResizeListener(listener: () => void): void;
    addDocumentListener(type: 'focusin' | 'focusout' | 'scroll', listener: (ev: Event) => void): void;
    removeDocumentListener(type: 'focusin' | 'focusout' | 'scroll', listener: (ev: Event) => void): void;
    requestFrame(callback: () => void): number;
    cancelFrame(handle: number): void;
    setIntervalTimer(callback: () => void, ms: number): number;
    clearIntervalTimer(handle: number): void;
    /** The root scroller's scrollTop. Safari's keyboard reveal scrolls the
     * root AND reports the same displacement as visualViewport.offsetTop,
     * and the dialogs ride the root scroll (their rects shift with it), so
     * a band at offsetTop alone lands one keyboard-pan too low. */
    getRootScrollY(): number;
}

/** On a dialog whose position this manager currently owns. Suppresses
 * bootstrap's transform transition (app-dialog.less) and the task-edit
 * dialog's own keep-on-screen clamp. */
export const KEYBOARD_AVOID_CLASS = 'gp-keyboard-avoid';

interface AppliedFit {
    translateY: number;
}

export class ModalKeyboardAvoidance {
    private openCount = 0;
    private viewport: IViewportSource | null = null;
    private frameHandle: number | null = null;
    private heartbeatHandle: number | null = null;
    private lastGeometryKey = '';
    private readonly applied = new Map<HTMLElement, AppliedFit>();
    private readonly onVvEvent = () => { this.debug.vvEvents++; this.wake('vv'); };
    private readonly onWindowEvent = () => { this.debug.winEvents++; this.wake('win'); };
    private readonly onDocScroll = () => { this.wake('scr'); };
    // Safari has now been caught settling keyboard geometry with no event we
    // subscribe to (the due-date/%-complete pan on Crane's iPad survived vv
    // resize+scroll, window resize AND focus wake sources), so a slow poll is
    // the wake source of last resort. Only runs while a modal is open on a
    // tablet, and only wakes when geometry actually moved or a field is
    // focused (typing is exactly when Safari re-pans silently).
    private readonly onHeartbeat = () => {
        if (!this.viewport) {
            return;
        }
        if (this.focusedElement || this.geometryKey() !== this.lastGeometryKey) {
            this.wake('hb');
        }
    };

    // Focus is the only keyboard signal desktop-mode iPad Safari gives a
    // page: the on-screen keyboard overlays without changing innerHeight,
    // documentElement.clientHeight or visualViewport (proven on device four
    // ways, including the task edit dialog's old innerHeight clamp never
    // once rescuing it). Track where focus is; geometry gets estimated in
    // apply() when the platform reports none.
    private focusedElement: Element | null = null;
    private readonly onFocusIn = (ev: Event) => {
        this.debug.focusEvents++;
        this.focusedElement = ev.target instanceof Element ? ev.target : null;
        this.armFocusLog();
        this.wakeReasons.push('fin');
        // Synchronous, not rAF-deferred: Safari picks its keyboard pan target
        // from the field's position DURING focus processing. A deferred fit
        // leaves Safari aiming at the field's pre-lift position, and it pans
        // the viewport hundreds of px to where the field no longer is (the
        // due-date / %-complete failure). Fitting inside the focusin handler
        // means Safari aims at the lifted position and needs no pan.
        this.apply();
    };
    private readonly onFocusOut = () => {
        this.focusedElement = null;
        this.scheduleApply();
    };

    /** Diagnostics for the ?kbddebug overlay; costs a few increments. */
    private readonly debug = { opens: 0, closes: 0, vvEvents: 0, winEvents: 0, focusEvents: 0, applies: 0, dialogs: 0, lastFit: '' };
    /** Wake tags accumulated since the last apply, drained into the log. */
    private readonly wakeReasons: string[] = [];
    /** Applies logged for 1.5s after each focusin, then FROZEN, so the
     * failure-time sequence survives the user scrolling around afterwards to
     * read the overlay (which itself fires more applies). */
    private readonly focusLog: string[] = [];
    private focusLogUntil = 0;
    private focusLogT0 = 0;

    private armFocusLog(): void {
        const el = this.focusedElement;
        const tag = el ? `${el.tagName}${el.id ? '#' + el.id : ''}.${String(el.className).slice(0, 24)}` : '?';
        this.focusLog.length = 0;
        this.focusLog.push(`FOCUS ${tag}`);
        this.focusLogT0 = Date.now();
        this.focusLogUntil = this.focusLogT0 + 1500;
    }

    private logApply(line: string): void {
        if (Date.now() >= this.focusLogUntil) {
            return;
        }
        if (this.focusLog.length < 13) {
            this.focusLog.push(line);
        } else {
            // Keep the first entries (engagement) and roll the tail (settling).
            this.focusLog.splice(4, 1);
            this.focusLog.push(line);
        }
    }

    private geometryKey(): string {
        const vp = this.viewport;
        return vp ? `${Math.round(vp.offsetTop)}|${Math.round(vp.height)}|${this.deps.getLayoutViewportHeight()}|${Math.round(this.deps.getRootScrollY())}` : '';
    }

    private wake(reason: string): void {
        this.wakeReasons.push(reason);
        this.scheduleApply();
    }

    getDebugSnapshot() {
        return {
            ...this.debug,
            openCount: this.openCount,
            attached: !!this.viewport,
            vp: this.viewport ? { top: Math.round(this.viewport.offsetTop), h: Math.round(this.viewport.height) } : null,
            focusLog: this.focusLog.slice(),
        };
    }

    constructor(private readonly deps: IModalKeyboardAvoidanceDeps) { }

    modalOpened(): void {
        this.openCount++;
        this.debug.opens++;
        // "not yet attached", never "first open": if one open ever found no
        // viewport, an openCount-based test would poison every later attach.
        if (!this.viewport && this.deps.isTablet()) {
            this.viewport = this.deps.getViewport() || null;
            if (this.viewport) {
                this.viewport.addEventListener('resize', this.onVvEvent);
                this.viewport.addEventListener('scroll', this.onVvEvent);
                // The keyboard reached Crane's iPad as a plain window resize
                // (the workspace re-laid out) with NO visualViewport event, so
                // the window is a wake-up source too; geometry still comes
                // from the viewport read at apply time.
                this.deps.addWindowResizeListener(this.onWindowEvent);
                this.deps.addDocumentListener('focusin', this.onFocusIn);
                this.deps.addDocumentListener('focusout', this.onFocusOut);
                this.deps.addDocumentListener('scroll', this.onDocScroll);
                this.heartbeatHandle = this.deps.setIntervalTimer(this.onHeartbeat, 250);
            }
        }
        // No fit at open: the task edit dialog positions itself in a later
        // $timeout, so an open-time fit measures pre-positioning geometry,
        // bakes in a stale transform, and nothing corrects it until a
        // keyboard event. Fitting starts with the first viewport event.
    }

    modalClosed(): void {
        this.openCount = Math.max(0, this.openCount - 1);
        this.debug.closes++;
        if (this.openCount === 0 && this.viewport) {
            this.viewport.removeEventListener('resize', this.onVvEvent);
            this.viewport.removeEventListener('scroll', this.onVvEvent);
            this.deps.removeWindowResizeListener(this.onWindowEvent);
            this.deps.removeDocumentListener('focusin', this.onFocusIn);
            this.deps.removeDocumentListener('focusout', this.onFocusOut);
            this.deps.removeDocumentListener('scroll', this.onDocScroll);
            if (this.heartbeatHandle !== null) {
                this.deps.clearIntervalTimer(this.heartbeatHandle);
                this.heartbeatHandle = null;
            }
            this.focusedElement = null;
            this.viewport = null;
            if (this.frameHandle !== null) {
                this.deps.cancelFrame(this.frameHandle);
                this.frameHandle = null;
            }
            this.restoreAll();
        }
    }

    private scheduleApply(): void {
        if (this.frameHandle !== null) {
            return;
        }
        this.frameHandle = this.deps.requestFrame(() => {
            this.frameHandle = null;
            this.apply();
        });
    }

    private apply(): void {
        const viewport = this.viewport;
        if (!viewport) {
            this.wakeReasons.length = 0;
            return;
        }
        this.debug.applies++;
        const reasons = this.wakeReasons.splice(0).join('+') || 'direct';
        this.lastGeometryKey = this.geometryKey();
        // Device-measured (23 Sep kbddebug log): Safari's keyboard reveal
        // scrolled the root by 173 AND reported offsetTop 173, while every
        // dialog rect shifted with the scroll. In dialog client coordinates
        // the visible band therefore starts at offsetTop MINUS the root
        // scroll (0 there), not at offsetTop; using offsetTop alone
        // double-counts the pan and parks the dialog under the keyboard.
        const bandOffsetTop = viewport.offsetTop - this.deps.getRootScrollY();

        // No "is the keyboard up?" test: modern iPadOS SHRINKS the layout
        // viewport to the keyboard instead of panning the visual one, so any
        // displacement test reads "no keyboard" there. Fitting against the
        // visual viewport unconditionally is correct under both models — with
        // no keyboard the band is the whole screen and the fit restores.
        const dialogs = this.deps.getDialogElements();
        this.debug.dialogs = dialogs.length;
        for (const el of Array.from(this.applied.keys())) {
            if (dialogs.indexOf(el) < 0) {
                this.applied.delete(el);
            }
        }

        for (const el of dialogs) {
            const prior = this.applied.get(el);
            const rect = el.getBoundingClientRect();
            // Back the transform out of the COMPUTED style, not our records:
            // a dialog can be mid-transition (bootstrap re-animates whenever
            // the suppressor class is off, e.g. right after a close), and the
            // computed matrix is correct even then, where a recorded value
            // reads mid-flight geometry as natural position.
            const matrix = getComputedStyle(el).transform;
            const currentTranslateY = matrix && matrix !== 'none' ? (parseFloat(matrix.split(',')[5]) || 0) : 0;
            const naturalTop = rect.top - currentTranslateY;

            // The smaller of the two viewport heights: when the keyboard
            // shrinks the layout without firing visualViewport events, the
            // viewport object's height can be stale at the full size, and a
            // stale full height reads as "no keyboard".
            //
            // Measured geometry ONLY - no keyboard is ever assumed from
            // focus. This device's keyboard always shows up as a vv shrink
            // (kbddebug-proven), so no shrink means no keyboard: the earlier
            // bottom-half estimate clipped a perfectly visible dialog when
            // Crane typed with the on-screen keyboard dismissed.
            const layoutHeight = this.deps.getLayoutViewportHeight();
            const effectiveHeight = Math.min(viewport.height, layoutHeight);
            const focusedField = this.focusedElement && el.contains(this.focusedElement) ? this.focusedElement : null;

            // The field rides the dialog's transform, so the same back-out
            // yields its natural position. Zero-size means a focus proxy or
            // hidden input; geometry from one is meaningless.
            let fieldMetrics: DialogNaturalMetrics | undefined;
            if (focusedField) {
                const fieldRect = focusedField.getBoundingClientRect();
                if (fieldRect.height > 0) {
                    fieldMetrics = { top: fieldRect.top - currentTranslateY, height: fieldRect.height };
                }
            }

            const fit = fitDialogToViewportBand(
                { offsetTop: bandOffsetTop, offsetLeft: viewport.offsetLeft, height: effectiveHeight, width: viewport.width },
                { top: naturalTop, height: rect.height },
                BAND_MARGIN_PX,
                fieldMetrics);

            // A dialog we have never displaced stays entirely untouched on a
            // neutral fit, so benign viewport events (Safari bars) never
            // claim it or disable its own keep-on-screen clamp.
            if (!prior && fit.translateY === 0) {
                continue;
            }
            this.debug.lastFit = `ty ${Math.round(fit.translateY)} top ${Math.round(naturalTop)} h ${Math.round(rect.height)} band ${Math.round(effectiveHeight)}`;

            // A no-op fit still keeps KEYBOARD_AVOID_CLASS: dropping it would
            // re-enable bootstrap's transform transition, and the next burst
            // of viewport events would measure the dialog mid-animation and
            // fit against garbage geometry. Full restore happens at close,
            // when no further passes can run.
            this.applyFit(el, fit);

            let fieldEnd = '';
            if (focusedField) {
                const bandTop = bandOffsetTop + BAND_MARGIN_PX;
                const bandBottom = bandOffsetTop + effectiveHeight - BAND_MARGIN_PX;
                const fr = focusedField.getBoundingClientRect();
                fieldEnd = ` f${Math.round(fr.top)} ${fr.top >= bandTop - 1 && fr.bottom <= bandBottom + 1 ? 'in' : 'OUT'}`;
            }

            this.logApply(`+${((Date.now() - this.focusLogT0) / 1000).toFixed(2)} ${reasons}`
                + ` o${Math.round(viewport.offsetTop)} s${Math.round(this.deps.getRootScrollY())} h${Math.round(viewport.height)} L${layoutHeight}`
                + ` d${Math.round(naturalTop)}/${Math.round(rect.height)} ty${Math.round(fit.translateY)}${fieldEnd}`);
        }
    }

    private applyFit(el: HTMLElement, fit: DialogFit): void {
        const translateY = Math.round(fit.translateY);
        el.style.transform = translateY === 0 ? '' : `translateY(${translateY}px)`;
        el.classList.add(KEYBOARD_AVOID_CLASS);
        this.applied.set(el, { translateY });
    }

    /** Close-time restore; also releases the transition suppressor. */

    private restore(el: HTMLElement): void {
        el.style.transform = '';
        el.classList.remove(KEYBOARD_AVOID_CLASS);
        this.applied.delete(el);
    }

    private restoreAll(): void {
        for (const el of Array.from(this.applied.keys())) {
            this.restore(el);
        }
    }
}

// #1470 diagnostics, kept deliberately (Crane, 23 Sep 2026): adding
// ?kbddebug to the URL paints a live readout of everything the manager sees,
// plus a frozen log of the applies after each focus, so one screenshot from
// a failing device answers which layer broke. Inert without the parameter.
// Keyboard geometry has shifted under us across iPadOS releases; this is
// the instrument that cracked the 2026 rounds, so it stays.
function armKeyboardDebugOverlay(manager: ModalKeyboardAvoidance): void {
    if (typeof window === 'undefined' || window.location.search.indexOf('kbddebug') < 0) {
        return;
    }
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;top:0;left:0;z-index:2147483647;pointer-events:none;'
        + 'background:rgba(0,0,0,.78);color:#7CFC00;font:10px/1.5 monospace;padding:4px 8px;white-space:pre;';
    const attach = () => document.body ? document.body.appendChild(el) : setTimeout(attach, 200);
    attach();
    setInterval(() => {
        const s = manager.getDebugSnapshot();
        const vv = window.visualViewport;
        // Fixed elements pin to the LAYOUT viewport, which Safari pans out
        // from under the visible area when the keyboard reveals a field, so
        // glue the overlay to the visual viewport or the failure moment is
        // exactly when it cannot be read.
        if (vv) {
            el.style.top = Math.round(vv.offsetTop) + 'px';
            el.style.left = Math.round(vv.offsetLeft) + 'px';
        }
        el.textContent =
            `innerH ${window.innerHeight} clientH ${document.documentElement.clientHeight}\n`
            + `vv ${vv ? Math.round(vv.height) + ' top ' + Math.round(vv.offsetTop) : 'MISSING'}\n`
            + `tablet ${String((window.document.body || el).classList.contains('gp-tablet'))} attached ${String(s.attached)} openCount ${s.openCount}\n`
            + `opens ${s.opens} closes ${s.closes} vvEv ${s.vvEvents} winEv ${s.winEvents} focusEv ${s.focusEvents} applies ${s.applies} dialogs ${s.dialogs}\n`
            + `lastFit ${s.lastFit || '-'}\n`
            + s.focusLog.join('\n');
    }, 300);
}

export const modalKeyboardAvoidance = new ModalKeyboardAvoidance({
    getViewport: () => window.visualViewport,
    isTablet: () => BrowserFeatureDetector.isSystemTablet(),
    getLayoutViewportHeight: () => document.documentElement.clientHeight,
    addWindowResizeListener: listener => window.addEventListener('resize', listener),
    removeWindowResizeListener: listener => window.removeEventListener('resize', listener),
    addDocumentListener: (type, listener) => document.addEventListener(type, listener, true),
    removeDocumentListener: (type, listener) => document.removeEventListener(type, listener, true),
    // windowClass lands on the .modal window element; the dialog box is its direct child.
    getDialogElements: () => Array.from(document.querySelectorAll<HTMLElement>('.modal > .modal-dialog')),
    requestFrame: callback => window.requestAnimationFrame(callback),
    cancelFrame: handle => window.cancelAnimationFrame(handle),
    setIntervalTimer: (callback, ms) => window.setInterval(callback, ms),
    clearIntervalTimer: handle => window.clearInterval(handle),
    getRootScrollY: () => window.pageYOffset || 0,
});

armKeyboardDebugOverlay(modalKeyboardAvoidance);
